import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft,
  Search,
  DollarSign,
  Tag,
  Plus,
  Check,
  X,
  AlertCircle,
  Trash2,
  ToggleLeft,
  ToggleRight,
  ScanLine,
} from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';

interface Product {
  id: string;
  name: string;
  brand: string;
  sku?: string;
  category?: string;
}

interface CollectedPrice {
  id: string;
  product_id: string;
  product_name: string;
  sku?: string;
  price: number | string;
  is_promotion: boolean;
  promotion_type?: string;
  photo_url?: string;
  recorded_at: string;
}

type PromoType = 'DISCOUNT' | 'BUY_X_PAY_Y' | 'GIFT' | 'OTHER';

const PROMO_TYPES: { value: PromoType; label: string }[] = [
  { value: 'DISCOUNT', label: 'Desconto' },
  { value: 'BUY_X_PAY_Y', label: 'Leve X Pague Y' },
  { value: 'GIFT', label: 'Brinde' },
  { value: 'OTHER', label: 'Outro' },
];

export default function PriceCollection({ visitId, storeId }: { visitId: string; storeId: string }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showResults, setShowResults] = useState(false);

  const [showScanner, setShowScanner] = useState(false);

  // Form state
  const [regularPrice, setRegularPrice] = useState('');
  const [promoPrice, setPromoPrice] = useState('');
  const [promoType, setPromoType] = useState<PromoType>('DISCOUNT');
  const [isCompetitor, setIsCompetitor] = useState(false);
  const [competitorBrand, setCompetitorBrand] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search products
  const { data: searchResults, isLoading: isSearching } = useQuery<Product[]>({
    queryKey: ['products', 'search', debouncedSearch],
    queryFn: () => api.get(`/products?search=${encodeURIComponent(debouncedSearch)}&limit=10`).then(res => res.data.data),
    enabled: debouncedSearch.length >= 2,
  });

  // Fetch collected prices
  const { data: collectedPrices, isLoading: isLoadingPrices } = useQuery<CollectedPrice[]>({
    queryKey: ['prices', visitId],
    queryFn: () => api.get(`/prices?visit_id=${visitId}`).then(res => res.data.data),
  });

  // Submit price mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProduct) throw new Error('Selecione um produto');
      if (!regularPrice) throw new Error('Informe o preco regular');

      const payload: Record<string, unknown> = {
        visit_id: visitId,
        product_id: selectedProduct.id,
        price: parseFloat(regularPrice),
        is_promotion: !!promoPrice,
      };

      if (promoPrice) {
        payload.promotion_type = promoType;
      }

      const response = await api.post('/prices', payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prices', visitId] });
      resetForm();
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || error.message || 'Erro ao registrar preco');
    },
  });

  const resetForm = () => {
    setSelectedProduct(null);
    setSearchTerm('');
    setDebouncedSearch('');
    setRegularPrice('');
    setPromoPrice('');
    setPromoType('DISCOUNT');
    setIsCompetitor(false);
    setCompetitorBrand('');
    setShowResults(false);
    searchInputRef.current?.focus();
  };

  const handleBarcodeScan = async (barcode: string) => {
    setShowScanner(false);
    try {
      const res = await api.get(`/products/barcode/${encodeURIComponent(barcode)}`);
      handleSelectProduct(res.data.data);
    } catch {
      alert(`Produto não encontrado para código: ${barcode}`);
    }
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setSearchTerm(product.name);
    setShowResults(false);
  };

  const handleSubmit = () => {
    if (!selectedProduct) {
      alert('Selecione um produto');
      return;
    }
    if (!regularPrice || parseFloat(regularPrice) <= 0) {
      alert('Informe um preco regular valido');
      return;
    }
    if (isCompetitor && !competitorBrand.trim()) {
      alert('Informe a marca do concorrente');
      return;
    }
    submitMutation.mutate();
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const getPromoLabel = (type: string) => {
    const found = PROMO_TYPES.find(p => p.value === type);
    return found ? found.label : type;
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation(`/visit/${storeId}`)}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Coleta de Precos</h1>
            {collectedPrices && (
              <p className="text-sm opacity-80">{collectedPrices.length} preco(s) coletado(s)</p>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 space-y-4">
        {/* Product Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Buscar Produto
          </h3>
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedProduct(null);
                  setShowResults(true);
                }}
                onFocus={() => setShowResults(true)}
                placeholder="Digite o nome do produto..."
                className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedProduct(null);
                    setShowResults(false);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowScanner(true)}
              className="flex items-center justify-center w-12 h-12 bg-primary text-white rounded-xl active:bg-primary/90"
              title="Escanear código de barras"
            >
              <ScanLine className="h-5 w-5" />
            </button>
          </div>

          {/* Search Results Dropdown */}
          {showResults && debouncedSearch.length >= 2 && !selectedProduct && (
            <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden bg-white max-h-60 overflow-y-auto">
              {isSearching ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm">Buscando...</p>
                </div>
              ) : searchResults && searchResults.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {searchResults.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleSelectProduct(product)}
                      className="w-full text-left p-3 active:bg-gray-50"
                    >
                      <p className="font-medium text-gray-900">{product.name}</p>
                      <p className="text-sm text-gray-500">
                        {product.brand}
                        {product.sku && ` - SKU: ${product.sku}`}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <p className="text-sm">Nenhum produto encontrado</p>
                </div>
              )}
            </div>
          )}

          {/* Selected Product */}
          {selectedProduct && (
            <div className="mt-3 flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-xl">
              <div className="p-2 rounded-full bg-primary/10">
                <Tag className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{selectedProduct.name}</p>
                <p className="text-sm text-gray-500 truncate">{selectedProduct.brand}</p>
              </div>
              <button
                onClick={() => {
                  setSelectedProduct(null);
                  setSearchTerm('');
                  setShowResults(false);
                }}
                className="p-1 text-gray-400"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Price Form - Only shown when product is selected */}
        {selectedProduct && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
            {/* Regular Price */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Preco Regular <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={regularPrice}
                  onChange={(e) => setRegularPrice(e.target.value)}
                  placeholder="0,00"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            </div>

            {/* Promo Price */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Preco Promocional
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={promoPrice}
                  onChange={(e) => setPromoPrice(e.target.value)}
                  placeholder="0,00"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            </div>

            {/* Promo Type - Only shown when promo price is entered */}
            {promoPrice && parseFloat(promoPrice) > 0 && (
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">
                  Tipo Promocao
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PROMO_TYPES.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setPromoType(type.value)}
                      className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                        promoType === type.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-100 text-gray-700 active:bg-gray-50'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Competitor Toggle */}
            <div>
              <button
                onClick={() => setIsCompetitor(!isCompetitor)}
                className="w-full flex items-center justify-between p-3 rounded-xl border border-gray-200 active:bg-gray-50"
              >
                <span className="text-sm font-semibold text-gray-700">Concorrente?</span>
                {isCompetitor ? (
                  <ToggleRight className="h-7 w-7 text-primary" />
                ) : (
                  <ToggleLeft className="h-7 w-7 text-gray-300" />
                )}
              </button>
            </div>

            {/* Competitor Brand - Only shown when competitor is toggled on */}
            {isCompetitor && (
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">
                  Marca Concorrente <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={competitorBrand}
                  onChange={(e) => setCompetitorBrand(e.target.value)}
                  placeholder="Nome da marca concorrente..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitMutation.isPending || !regularPrice}
              className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" />
                  <span>Registrar Preco</span>
                </>
              )}
            </button>

            {/* Error Message */}
            {submitMutation.isError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">
                    {(submitMutation.error as any)?.response?.data?.error?.message ||
                      (submitMutation.error as any)?.message ||
                      'Erro ao registrar preco'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Collected Prices List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              <h2 className="font-semibold text-gray-900">Precos Coletados</h2>
            </div>
          </div>

          {isLoadingPrices ? (
            <div className="p-8 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm">Carregando...</p>
            </div>
          ) : !collectedPrices || collectedPrices.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm">Nenhum preco coletado ainda</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {collectedPrices.map((price) => (
                <div key={price.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${price.is_promotion ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {price.is_promotion ? (
                        <Tag className="h-4 w-4 text-green-600" />
                      ) : (
                        <DollarSign className="h-4 w-4 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{price.product_name}</p>
                      {price.sku && (
                        <p className="text-xs text-gray-500">SKU: {price.sku}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm font-semibold text-gray-900">
                          {formatCurrency(Number(price.price))}
                        </span>
                        {price.is_promotion && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                            Promoção
                          </span>
                        )}
                      </div>
                      {price.promotion_type && (
                        <span className="inline-flex items-center mt-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                          {getPromoLabel(price.promotion_type)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}
